import { Button } from '@thanx/uikit/button'
import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import type { PlaybackState } from './simulatorTypes'
import { TOUR_ANCHORS } from './tourAnchors'

const t = buildTranslate('recovery_ai.sandbox.playback')

const DOT_SIZE = '10px'

type Props = {
  state: PlaybackState
  onPrevious: () => void
  onNext: () => void
  onComplete: () => void
}

const PlaybackNav = ({ state, onPrevious, onNext, onComplete }: Props) => {
  const [css] = useStyletron()

  // i18n key per dot so done / active / upcoming are accessibly distinguishable
  // (not by color alone). Early-return helper avoids a nested ternary.
  const dotLabelKey = (i: number): string => {
    if (i === state.index) return 'step_current'
    if (i < state.index) return 'step_done'
    return 'step'
  }

  // Upcoming dots use grey70, not grey20: grey20 (#e9eaec) on white is ~1.2:1,
  // failing WCAG 1.4.11 non-text contrast (needs 3:1); grey70 (#6a747f) is ~4.8:1.
  // (grey50 is only ~2.3:1, so it would still fail.) Done/active dots stay
  // belizeHole50 (~5:1). Color is a redundant cue — aria-current + the per-dot
  // label carry done/active/upcoming state for assistive tech.
  const dotColor = (i: number): string =>
    i <= state.index
      ? merchantTheme.colors.belizeHole50
      : merchantTheme.colors.grey70

  return (
    <div
      data-tour={TOUR_ANCHORS.playbackNav}
      className={css({ display: 'flex', alignItems: 'center' })}
    >
      {/* Equal-flex side wrappers keep the dot tracker dead-centered regardless
          of the side buttons' widths — otherwise the wider "Complete" button on
          the last step (vs "Next") shifts the dots left. */}
      <div
        className={css({
          flex: 1,
          display: 'flex',
          justifyContent: 'flex-start',
        })}
      >
        <Button kind="secondary" disabled={state.isFirst} onClick={onPrevious}>
          {t('previous')}
        </Button>
      </div>

      <div
        className={css({
          flex: '0 0 auto',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        })}
      >
        {Array.from({ length: state.total }, (_, i) => (
          <span
            key={i}
            role="img"
            aria-label={t(dotLabelKey(i), {
              number: i + 1,
              total: state.total,
            })}
            aria-current={i === state.index ? 'step' : undefined}
            className={css({
              width: DOT_SIZE,
              height: DOT_SIZE,
              borderRadius: '50%',
              backgroundColor: dotColor(i),
            })}
          />
        ))}
      </div>

      <div
        className={css({
          flex: 1,
          display: 'flex',
          justifyContent: 'flex-end',
        })}
      >
        {state.isLast ? (
          <Button kind="primary" onClick={onComplete}>
            {t('complete')}
          </Button>
        ) : (
          <Button kind="primary" onClick={onNext}>
            {t('next')}
          </Button>
        )}
      </div>
    </div>
  )
}

export default PlaybackNav
